import React, { useEffect, useRef, useState } from "react";

export const uid = () => Math.random().toString(36).slice(2, 10);
export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** Tiny safe markdown: headings, bold, italics, links, images, lists, quotes, hr. Returns React nodes. */
export function Markdown({ text }: { text: string }) {
  const lines = (text || "").split("\n");
  const out: React.ReactNode[] = [];
  let list: React.ReactNode[] | null = null;
  const flush = () => {
    if (list) out.push(<ul key={out.length}>{list}</ul>);
    list = null;
  };
  lines.forEach((ln, i) => {
    const h = ln.match(/^(#{1,3})\s+(.*)$/);
    if (h) { flush(); const T = (`h${h[1].length + 2}`) as any; out.push(<T key={i}>{inline(h[2])}</T>); return; }
    if (/^\s*[-*]\s+/.test(ln)) { (list ||= []).push(<li key={i}>{inline(ln.replace(/^\s*[-*]\s+/, ""))}</li>); return; }
    flush();
    if (/^---+\s*$/.test(ln)) { out.push(<hr key={i} />); return; }
    if (/^>\s?/.test(ln)) { out.push(<blockquote key={i}>{inline(ln.replace(/^>\s?/, ""))}</blockquote>); return; }
    if (!ln.trim()) { out.push(<div key={i} className="md-gap" />); return; }
    out.push(<p key={i}>{inline(ln)}</p>);
  });
  flush();
  return <div className="md">{out}</div>;
}

function inline(s: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_|`([^`]+)`/g;
  let last = 0, m: RegExpExecArray | null, k = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[2] && safeUrl(m[2])) out.push(<img key={k++} src={m[2]} alt={m[1]} className="md-img" />);
    else if (m[4] && safeUrl(m[4])) out.push(<a key={k++} href={m[4]} target="_blank" rel="noreferrer noopener">{m[3]}</a>);
    else if (m[5]) out.push(<b key={k++}>{m[5]}</b>);
    else if (m[6] || m[7]) out.push(<i key={k++}>{m[6] || m[7]}</i>);
    else if (m[8]) out.push(<code key={k++}>{m[8]}</code>);
    else out.push(m[0]);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
const safeUrl = (u: string) => /^(https?:\/\/|\/files\/)/.test(u);

/** Input that commits on blur/Enter and keeps local edits while focused. */
export function Field(props: {
  value: string | number | undefined;
  onCommit: (v: string) => void;
  className?: string;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
  style?: React.CSSProperties;
  title?: string;
  disabled?: boolean;
  select?: boolean;
}) {
  const [v, setV] = useState(String(props.value ?? ""));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setV(String(props.value ?? "")); }, [props.value]);
  const commit = () => { if (v !== String(props.value ?? "")) props.onCommit(v); };
  const common = {
    value: v,
    className: props.className,
    placeholder: props.placeholder,
    style: props.style,
    title: props.title,
    disabled: props.disabled,
    onFocus: (e: any) => { focused.current = true; if (props.select) e.target.select(); },
    onBlur: () => { focused.current = false; commit(); },
    onChange: (e: any) => setV(e.target.value),
  };
  if (props.multiline) return <textarea {...common} rows={props.rows ?? 4} />;
  return <input {...common} type={props.type || "text"} onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />;
}

// ---- modal prompt / confirm ----
type Dlg = { kind: "prompt" | "confirm" | "choose"; title: string; value?: string; options?: { label: string; value: string }[]; resolve: (v: any) => void };
let setDlg: ((d: Dlg | null) => void) | null = null;

export function prompt(title: string, value = ""): Promise<string | null> {
  return new Promise(resolve => setDlg?.({ kind: "prompt", title, value, resolve }));
}
export function confirm(title: string): Promise<boolean> {
  return new Promise(resolve => setDlg?.({ kind: "confirm", title, resolve }));
}
export function choose(title: string, options: { label: string; value: string }[]): Promise<string | null> {
  return new Promise(resolve => setDlg?.({ kind: "choose", title, options, value: options[0]?.value, resolve }));
}

export function DialogHost() {
  const [d, setD] = useState<Dlg | null>(null);
  const [val, setVal] = useState("");
  useEffect(() => {
    setDlg = x => { setD(x); setVal(x?.value ?? ""); };
    return () => { setDlg = null; };
  }, []);
  if (!d) return null;
  const done = (v: any) => { d.resolve(v); setD(null); };
  return (
    <div className="modal-bg" onMouseDown={e => e.target === e.currentTarget && done(d.kind === "confirm" ? false : null)}>
      <form className="modal" onSubmit={e => { e.preventDefault(); done(d.kind === "confirm" ? true : val); }}>
        <div className="modal-title">{d.title}</div>
        {d.kind === "prompt" && <input autoFocus value={val} onChange={e => setVal(e.target.value)} onFocus={e => e.target.select()} />}
        {d.kind === "choose" && (
          <select autoFocus value={val} onChange={e => setVal(e.target.value)} size={Math.min(8, d.options!.length)}>
            {d.options!.map(o => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <div className="modal-btns">
          <button type="button" onClick={() => done(d.kind === "confirm" ? false : null)}>Cancel</button>
          <button type="submit" className="primary" autoFocus={d.kind === "confirm"}>OK</button>
        </div>
      </form>
    </div>
  );
}

export function fmtSize(b: number) {
  return b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.ceil(b / 1e3)} KB`;
}

export function readFileAsText(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = rej;
    r.readAsText(f);
  });
}
